'use client';

import { c as _c } from "react/compiler-runtime";
import { createAuthFetch, useAuth } from '@repo/auth/client';
import { useDotEnv } from '@repo/dot-env/client';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { up } from 'up-fetch';
import { getScopeOrganisationId, type OrganisationScope, scopeUpFetch, type UpFetch } from './organisation-scope.ts';
type Context = {
  upFetch: UpFetch;
};
type UpFetchProviderProps = {
  scope?: OrganisationScope;
};
const UpFetchContext = createContext<Context | null>(null);

/**
 * Requests and query keys follow `scope`, all organisations unless set. The upFetch holds across
 * renders until the scope's Organisation or the session changes, so `useApi()` and effects that
 * depend on it do too.
 */
export const UpFetchProvider = t0 => {
  const $ = _c(28);
  const {
    children,
    scope: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {
      kind: "all"
    } : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scope = t2;
  const {
    getSessionToken,
    getSessionVersion,
    logout,
    refreshToken,
    restoreImpersonationSession
  } = useAuth();
  const {
    API_URL,
    SOURCE
  } = useDotEnv();
  let t3;
  if ($[2] !== scope) {
    t3 = getScopeOrganisationId(scope);
    $[2] = scope;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const organisationId = t3;
  let t4;
  if ($[4] !== API_URL || $[5] !== SOURCE || $[6] !== getSessionToken || $[7] !== getSessionVersion || $[8] !== logout || $[9] !== organisationId || $[10] !== refreshToken || $[11] !== restoreImpersonationSession) {
    let t5;
    if ($[13] !== getSessionVersion || $[14] !== logout) {
      t5 = async sessionVersion => {
        if (sessionVersion !== undefined && sessionVersion !== getSessionVersion()) {
          return;
        }
        const logoutPromise = logout();
        const logoutSessionVersion = getSessionVersion();
        await logoutPromise;
        if (logoutSessionVersion === getSessionVersion()) {
          window.location.assign("/");
        }
      };
      $[13] = getSessionVersion;
      $[14] = logout;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== getSessionVersion || $[17] !== logout || $[18] !== restoreImpersonationSession) {
      t6 = async sessionVersion_0 => {
        if (sessionVersion_0 !== undefined && sessionVersion_0 !== getSessionVersion()) {
          return;
        }
        const restoration = restoreImpersonationSession(sessionVersion_0);
        const recoverySessionVersion = getSessionVersion();
        const restored = await restoration;
        if (recoverySessionVersion !== getSessionVersion()) {
          return;
        }
        if (restored) {
          window.location.reload();
          return;
        }
        const logoutPromise_0 = logout();
        const logoutSessionVersion_0 = getSessionVersion();
        await logoutPromise_0;
        if (logoutSessionVersion_0 === getSessionVersion()) {
          window.location.assign("/");
        }
      };
      $[16] = getSessionVersion;
      $[17] = logout;
      $[18] = restoreImpersonationSession;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    const authFetch = createAuthFetch({
      getSessionToken,
      getSessionVersion,
      onAuthError: t5,
      onImpersonationExpired: t6,
      refreshToken
    });
    let t7;
    if ($[20] !== API_URL || $[21] !== SOURCE) {
      t7 = () => ({
        baseUrl: API_URL,
        headers: {
          Accept: "application/json",
          "App-Source": SOURCE
        }
      });
      $[20] = API_URL;
      $[21] = SOURCE;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    const upFetch = up(authFetch, t7);
    const scoped = organisationId === undefined ? {
      kind: "all"
    } : {
      id: organisationId,
      kind: "organisation"
    };
    t4 = scopeUpFetch(upFetch, scoped);
    $[4] = API_URL;
    $[5] = SOURCE;
    $[6] = getSessionToken;
    $[7] = getSessionVersion;
    $[8] = logout;
    $[9] = organisationId;
    $[10] = refreshToken;
    $[11] = restoreImpersonationSession;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[23] !== t4) {
    t5 = {
      upFetch: t4
    };
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const value = t5;
  let t6;
  if ($[25] !== children || $[26] !== value) {
    t6 = <UpFetchContext.Provider value={value} data-sentry-element="UpFetchContext.Provider" data-sentry-component="UpFetchProvider" data-sentry-source-file="upfetch-client.tsx">{children}</UpFetchContext.Provider>;
    $[25] = children;
    $[26] = value;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
};
export const useUpFetch = () => {
  const context = useContext(UpFetchContext);
  if (!context) {
    throw new Error("useUpFetch must be used within a UpFetchContextProvider");
  }
  return context.upFetch;
};

/** An upFetch without the session or an Organisation, for public requests such as signing in. */
export const useBaseUpFetch = () => {
  const $ = _c(2);
  const env = useDotEnv();
  let t0;
  if ($[0] !== env) {
    t0 = up(fetch, () => ({
      baseUrl: env.API_URL,
      headers: {
        Accept: "application/json",
        "App-Source": env.SOURCE
      }
    }));
    $[0] = env;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};